import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { FiUser, FiMail, FiGlobe, FiMessageSquare, FiShield, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLanguage } from '../../contexts/LanguageContext';

// THEME color for "valid"
const THEME_BLUE = '#2b5baa';

// Validation colors (success now uses theme blue)
const COLORS = {
  border: { default: '#e5e8ee', error: '#ef4444', success: THEME_BLUE },
  icon:   { default: '#9aa0a6', error: '#ef4444', success: THEME_BLUE },
};

const FIELD_LABEL = {
  name: 'NAME',
  email: 'EMAIL',
  message: 'MESSAGE',
  captcha: 'CAPTCHA',
};

const errorMsg = (field, code, translations) => {
  if (!code) return '';
  if (code === 'required') return `${translations[FIELD_LABEL[field]] || 'Field'} ${translations.REQUIRED}`;
  if (field === 'email' && code === 'invalid') return translations.INVALID_EMAIL;
  return 'Invalid value.';
};

// ADD: Field label component so titles render above inputs
const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4, color: '#111827' }}
  >
    {children}
  </label>
);

// Add: FieldWithIcon helper (prevents remounts)
const FieldWithIcon = React.memo(({ icon, borderColor = '#e5e8ee', iconColor = '#9aa0a6', children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8f9fa', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '10px 12px' }}>
    <span style={{ color: iconColor, display: 'inline-flex' }}>{icon}</span>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
));

// Add: API/env keys
const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactAreaTwo = ({
  CONTACT_HEADER = "We can take your business to the next level.",
  CONTACT_LABEL = "contact",
  NAME = "Name",
  EMAIL = "Email",
  COMPANY = "Company (optional)",
  WEBSITE = "Company website (optional)",
  MESSAGE = "Message",
  ENTER_NAME = "Enter your name here",
  ENTER_EMAIL = "Enter email address here",
  ENTER_COMPANY = "Enter your company name",
  ENTER_WEBSITE = "Enter your company website",
  ENTER_MESSAGE = "Enter message here",
  CAPTCHA_PLACEHOLDER = "Enter CAPTCHA",
  REFRESH_CAPTCHA = "Refresh CAPTCHA",
  AGREE = "I agree to the",
  TERMS = "Terms & Conditions",
  LEGAL = "By submitting this form, I agree that the information entered will be used as part of the relationship that may develop with Eagle Vision and its partners, fostering a mutually beneficial interaction between the parties involved.",
  SENDING = "Sending…",
  SUBMIT = "Submit Request",
  REQUIRED = "is required.",
  INVALID_EMAIL = "Please enter a valid email address.",
  FILL_ALL = "Please fill in all required fields and CAPTCHA.",
  MUST_AGREE = "You must agree to the terms before submitting.",
  MSG_SENT = "Message sent. We will get back to you soon.",
  MSG_FAIL = "Failed to send. Please try again.",
  CAPTCHA_LOAD_FAIL = "Unable to load CAPTCHA. Please refresh.",
}) => {
  const { currentLanguage, translateContent } = useLanguage();
  const [translations, setTranslations] = useState({
    CONTACT_HEADER, CONTACT_LABEL, NAME, EMAIL, COMPANY, WEBSITE, MESSAGE,
    ENTER_NAME, ENTER_EMAIL, ENTER_COMPANY, ENTER_WEBSITE, ENTER_MESSAGE,
    CAPTCHA_PLACEHOLDER, REFRESH_CAPTCHA, AGREE, TERMS, LEGAL,
    SENDING, SUBMIT, REQUIRED, INVALID_EMAIL, FILL_ALL, MUST_AGREE,
    MSG_SENT, MSG_FAIL, CAPTCHA_LOAD_FAIL,
  });

  useEffect(() => {
    const translateAll = async () => {
      const keys = [
        'CONTACT_HEADER', 'CONTACT_LABEL', 'NAME', 'EMAIL', 'COMPANY', 'WEBSITE', 'MESSAGE',
        'ENTER_NAME', 'ENTER_EMAIL', 'ENTER_COMPANY', 'ENTER_WEBSITE', 'ENTER_MESSAGE',
        'CAPTCHA_PLACEHOLDER', 'REFRESH_CAPTCHA', 'AGREE', 'TERMS', 'LEGAL',
        'SENDING', 'SUBMIT', 'REQUIRED', 'INVALID_EMAIL', 'FILL_ALL', 'MUST_AGREE',
        'MSG_SENT', 'MSG_FAIL', 'CAPTCHA_LOAD_FAIL',
      ];
      const originalTexts = {
        CONTACT_HEADER, CONTACT_LABEL, NAME, EMAIL, COMPANY, WEBSITE, MESSAGE,
        ENTER_NAME, ENTER_EMAIL, ENTER_COMPANY, ENTER_WEBSITE, ENTER_MESSAGE,
        CAPTCHA_PLACEHOLDER, REFRESH_CAPTCHA, AGREE, TERMS, LEGAL,
        SENDING, SUBMIT, REQUIRED, INVALID_EMAIL, FILL_ALL, MUST_AGREE,
        MSG_SENT, MSG_FAIL, CAPTCHA_LOAD_FAIL,
      };

      const newTranslations = {};

      const translationPromises = keys.map(async (key) => {
        const translated = await translateContent(originalTexts[key]);
        return { key, translated };
      });

      const results = await Promise.all(translationPromises);

      results.forEach(({ key, translated }) => {
        newTranslations[key] = translated;
      });

      setTranslations(newTranslations);
    };

    translateAll();
  }, [currentLanguage,
    CONTACT_HEADER, CONTACT_LABEL, NAME, EMAIL, COMPANY, WEBSITE, MESSAGE,
    ENTER_NAME, ENTER_EMAIL, ENTER_COMPANY, ENTER_WEBSITE, ENTER_MESSAGE,
    CAPTCHA_PLACEHOLDER, REFRESH_CAPTCHA, AGREE, TERMS, LEGAL,
    SENDING, SUBMIT, REQUIRED, INVALID_EMAIL, FILL_ALL, MUST_AGREE,
    MSG_SENT, MSG_FAIL, CAPTCHA_LOAD_FAIL, translateContent]);

  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Add: CAPTCHA state
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaImg, setCaptchaImg] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // Controlled form values (phone removed)
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    Website: '',
    message: '',
  });
  const update = (field) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: v };
      setErrors(validate(next, captchaValue));
      return next;
    });
  };

  // Add: touched/submission/email blur gates
  const [errors, setErrors] = useState({});
  const [, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [emailBlurred, setEmailBlurred] = useState(false);

  const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const validate = (values, cap) => {
    const e = {};
    if (!values.name.trim()) e.name = 'required';
    if (!values.email.trim()) e.email = 'required';
    else if (!emailOk(values.email.trim())) e.email = 'invalid';
    if (!values.message.trim()) e.message = 'required';
    if (!cap?.trim()) e.captcha = 'required';
    return e;
  };

  // Activate visuals only after Submit, except email which activates on blur too
  const statusOf = (field) => {
    const active = field === 'email' ? (emailBlurred || submitted) : submitted;
    if (!active) return 'default';
    return errors[field] ? 'error' : 'success';
  };

  const pickColors = (status) => ({
    borderColor: COLORS.border[status],
    iconColor: COLORS.icon[status],
  });

  const loadCaptcha = useCallback(async () => {
    try {
      setCaptchaLoading(true);
      const res = await fetch(`${API_URL}/contact/`, { method: 'GET' });
      const data = await res.json();
      if (data?.captcha_key) {
        setCaptchaKey(data.captcha_key);
        setCaptchaImg(buildCaptchaUrl(data.captcha_key));
        setCaptchaValue('');
      } else {
        throw new Error('Failed to get CAPTCHA key');
      }
    } catch {
      toast.error(translations.CAPTCHA_LOAD_FAIL);
    } finally {
      setCaptchaLoading(false);
    }
  }, [translations.CAPTCHA_LOAD_FAIL]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTouched((t) => ({ ...t, name: true, email: true, message: true, captcha: true }));

    const currentErrors = validate(form, captchaValue);
    setErrors(currentErrors);

    if (Object.keys(currentErrors).length > 0) {
      toast.info(translations.FILL_ALL);
      return;
    }
    if (!agreed) {
      toast.info(translations.MUST_AGREE);
      return;
    }

    setSending(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company_name: form.company.trim(),
        company_website: form.Website.trim(),
        message: form.message.trim(),
        captcha: captchaValue.trim(),
        captcha_key: captchaKey,
      };

      const res = await fetch(`${API_URL}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || (data?.errors ? Object.values(data.errors).flat().join(' ') : 'Validation failed.');
        toast.error(msg);
        await loadCaptcha();
        return;
      }

      await emailjs.sendForm(serviceId, templateId, formRef.current, { publicKey });
      toast.success(translations.MSG_SENT);

      setForm({ name: '', email: '', company: '', Website: '', message: '' });
      setTouched({});
      setErrors({});
      setAgreed(false);
      setCaptchaValue('');
      await loadCaptcha();
    } catch {
      toast.error(translations.MSG_FAIL);
    } finally {
      setSending(false);
    }
  };

  const inputStyle = { border: 'none', outline: 'none', background: 'transparent', width: '100%' };

  // colors per field (optional fields stay neutral)
  const nameColors = pickColors(statusOf('name'));
  const emailColors = pickColors(statusOf('email'));
  const messageColors = pickColors(statusOf('message'));
  const captchaColors = pickColors(statusOf('captcha'));
  const companyColors = pickColors('default');
  const websiteColors = pickColors('default');

  const markTouched = (field) => () => setTouched((t) => ({ ...t, [field]: true }));
  const buildCaptchaUrl = (key) => `${API_URL}/captcha/${encodeURIComponent(key)}/?t=${Date.now()}`;

  const FieldHeader = ({ htmlFor, label, errorId, showError, message }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 6,
      }}
    >
      <label htmlFor={htmlFor} style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111827' }}>
        {label}
      </label>
      <span
        id={errorId}
        style={{
          fontSize: 12,
          color: '#ef4444',
          height: 16,
          lineHeight: '16px',
          visibility: showError ? 'visible' : 'hidden',
        }}
      >
        {message || ' '}
      </span>
    </div>
  );

  return (
    <section className="contact__area-2">
      <div className="container">
        <div className="contact__form">
          <div className="container">
            <div className="row">
              <div className="col-lg-6">
                <div className="section__wrapper mb-45">
                  <h4 className="section__title">{translations.CONTACT_HEADER}</h4>
                  <div className="r-text"><span>{translations.CONTACT_LABEL}</span></div>
                </div>
              </div>

              <div className="col-xl-12">
                <form ref={formRef} onSubmit={handleSubmit} noValidate>
                  <div className="row">
                    {/* Name */}
                    <div className="col-lg-6">
                      <div style={{ marginBottom: 12 }}>
                        <FieldHeader
                          htmlFor="contact-name"
                          label={translations.NAME}
                          errorId="err-name"
                          showError={submitted && !!errors.name}
                          message={errorMsg('name', errors.name, translations)}
                        />
                        <FieldWithIcon
                          icon={<FiUser size={18} />}
                          borderColor={nameColors.borderColor}
                          iconColor={nameColors.iconColor}
                        >
                          <input
                            id="contact-name"
                            type="text"
                            name="name"
                            placeholder={translations.ENTER_NAME}
                            required
                            style={inputStyle}
                            value={form.name}
                            onChange={update('name')}
                            onBlur={markTouched('name')}
                            aria-invalid={submitted && !!errors.name}
                            aria-describedby="err-name"
                          />
                        </FieldWithIcon>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-lg-6">
                      <div style={{ marginBottom: 12 }}>
                        <FieldHeader
                          htmlFor="contact-email"
                          label={translations.EMAIL}
                          errorId="err-email"
                          showError={(emailBlurred || submitted) && !!errors.email}
                          message={errorMsg('email', errors.email, translations)}
                        />
                        <FieldWithIcon
                          icon={<FiMail size={18} />}
                          borderColor={emailColors.borderColor}
                          iconColor={emailColors.iconColor}
                        >
                          <input
                            id="contact-email"
                            type="email"
                            name="email"
                            placeholder={translations.ENTER_EMAIL}
                            required
                            style={inputStyle}
                            value={form.email}
                            onChange={update('email')}
                            onBlur={() => { setEmailBlurred(true); markTouched('email')(); }}
                            aria-invalid={(emailBlurred || submitted) && !!errors.email}
                            aria-describedby="err-email"
                          />
                        </FieldWithIcon>
                      </div>
                    </div>

                    {/* Company (optional) */}
                    <div className="col-lg-6">
                      <div style={{ marginBottom: 12 }}>
                        <FieldLabel htmlFor="contact-company">{translations.COMPANY}</FieldLabel>
                        <FieldWithIcon icon={<FiUser size={18} />} borderColor={companyColors.borderColor} iconColor={companyColors.iconColor}>
                          <input
                            id="contact-company"
                            type="text"
                            name="company"
                            placeholder={translations.ENTER_COMPANY}
                            style={inputStyle}
                            value={form.company}
                            onChange={update('company')}
                            onBlur={markTouched('company')}
                          />
                        </FieldWithIcon>
                      </div>
                    </div>

                    {/* Website (optional) */}
                    <div className="col-lg-6">
                      <div style={{ marginBottom: 12 }}>
                        <FieldLabel htmlFor="contact-website">{translations.WEBSITE}</FieldLabel>
                        <FieldWithIcon icon={<FiGlobe size={18} />} borderColor={websiteColors.borderColor} iconColor={websiteColors.iconColor}>
                          <input
                            id="contact-website"
                            type="url"
                            name="Website"
                            placeholder={translations.ENTER_WEBSITE}
                            style={inputStyle}
                            value={form.Website}
                            onChange={update('Website')}
                            onBlur={markTouched('Website')}
                          />
                        </FieldWithIcon>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: 12 }}>
                    <FieldHeader
                      htmlFor="contact-message"
                      label={translations.MESSAGE}
                      errorId="err-message"
                      showError={submitted && !!errors.message}
                      message={errorMsg('message', errors.message, translations)}
                    />
                    <div
                      style={{
                        position: 'relative',
                        background: '#f8f9fa',
                        border: `1px solid ${messageColors.borderColor}`,
                        borderRadius: 8,
                        padding: '10px 12px',
                      }}
                    >
                      <span style={{ position: 'absolute', left: 12, top: 10, color: messageColors.iconColor }}>
                        <FiMessageSquare size={18} />
                      </span>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        placeholder={translations.ENTER_MESSAGE}
                        style={{
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          width: '100%',
                          minHeight: 140,
                          resize: 'vertical',
                          paddingLeft: 32,
                          paddingTop: 2,
                        }}
                        value={form.message}
                        onChange={update('message')}
                        onBlur={markTouched('message')}
                        aria-invalid={submitted && !!errors.message}
                        aria-describedby="err-message"
                      />
                    </div>
                  </div>

                  {/* CAPTCHA (centered) */}
                  <div className="row justify-content-center mb-20">
                    <div className="col-12">
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 16,
                          flexWrap: 'wrap',
                          minHeight: 80,
                        }}
                      >
                        {/* CAPTCHA image */}
                        <div
                          style={{
                            width: 220,
                            minHeight: 60,
                            background: '#f5f7fa',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e5e8ee',
                          }}
                        >
                          {captchaImg ? (
                            <img src={captchaImg} alt="CAPTCHA" style={{ maxWidth: '100%', height: 40, objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: 12, color: '#666' }}>{translations.CAPTCHA_LOAD_FAIL}</span>
                          )}
                        </div>

                        {/* CAPTCHA input */}
                        <div style={{ minWidth: 220, maxWidth: 340, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FieldWithIcon
                            icon={<FiShield size={18} />}
                            borderColor={captchaColors.borderColor}
                            iconColor={captchaColors.iconColor}
                          >
                            <input
                              id="contact-captcha"
                              type="text"
                              name="captcha_input"
                              placeholder={translations.CAPTCHA_PLACEHOLDER}
                              value={captchaValue}
                              onChange={(e) => { setCaptchaValue(e.target.value); setErrors(validate(form, e.target.value)); }}
                              onBlur={markTouched('captcha')}
                              required
                              autoComplete="off"
                              style={inputStyle}
                              aria-invalid={submitted && !!errors.captcha}
                              aria-describedby="err-captcha"
                            />
                          </FieldWithIcon>
                          {/* Refresh icon */}
                          <button
                            type="button"
                            onClick={loadCaptcha}
                            disabled={captchaLoading || sending}
                            aria-label={translations.REFRESH_CAPTCHA}
                            title={translations.REFRESH_CAPTCHA}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 8,
                              cursor: captchaLoading || sending ? 'not-allowed' : 'pointer',
                              color: THEME_BLUE,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FiRefreshCw size={24} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hidden values for EmailJS */}
                  <input type="hidden" name="captcha_key" value={captchaKey} />
                  <input type="hidden" name="reply_to" value={form.email} />
                  <input type="hidden" name="greeting" value="Bonjour " />
                  <input type="hidden" name="receivedMessage" value="Nous avons bien reçu votre message. Voici un récapitulatif :" />
                  <input type="hidden" name="companyLabel" value="Nom du company" />
                  <input type="hidden" name="emailLabel" value="Adresse email" />
                  <input type="hidden" name="messageLabel" value="Message" />
                  <input type="hidden" name="urgentAssistance" value="Pour une assistance urgente, appelez" />
                  <input type="hidden" name="waitingMessage" value="En attendant, visitez " />
                  <input type="hidden" name="regards" value="Cordialement," />
                  <input type="hidden" name="websiteLink" value="eagle-vizion.com" />

                  {/* Agree + legal text */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
                    <input
                      type="checkbox"
                      id="agree"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      style={{ width: 18, height: 18, marginTop: 4 }}
                      required
                    />
                    <label htmlFor="agree" style={{ margin: 0 }}>
                      {translations.AGREE}
                      <Link to="/contact"> {translations.TERMS}</Link>
                      <div style={{ marginTop: 6, color: '#606770', fontSize: 14, lineHeight: 1.4 }}>
                        {translations.LEGAL}
                      </div>
                    </label>
                  </div>

                  <div className="form-submit text-center">
                    <button className="tp-btn" type="submit" disabled={sending}>
                      {sending ? translations.SENDING : translations.SUBMIT}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactAreaTwo;
